import {
  faMinus,
  faTrash,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

interface StickyNoteComponentProps {
  selectedAssets: string[];
  selectedAssetOrder: Record<string, number>;
  isVisible: boolean;
  position: { x: number; y: number };
  onPositionChange: (position: { x: number; y: number }) => void;
  onRemoveAsset: (assetCode: string) => void;
  onClearAll: () => void;
  onToggleVisibility: () => void;
  onAssetHover: (assetCode: string) => void;
  onAssetHoverEnd: () => void;
  onConfirmSelection?: () => void;
  confirmDisabled?: boolean;
  showRestoreButton?: boolean;
}

const StickyNoteComponent: React.FC<StickyNoteComponentProps> = ({
  selectedAssets,
  selectedAssetOrder,
  isVisible,
  position,
  onPositionChange,
  onRemoveAsset,
  onClearAll,
  onToggleVisibility,
  onAssetHover,
  onAssetHoverEnd,
  onConfirmSelection,
  confirmDisabled,
  showRestoreButton = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = (e: any) => {
    e.preventDefault();

    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e: any) => {
    if (isDragging) {
      onPositionChange({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handleDragMove);
      window.addEventListener("pointerup", handleDragEnd);
      window.addEventListener("pointercancel", handleDragEnd);
    }

    return () => {
      window.removeEventListener("pointermove", handleDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    };
  }, [isDragging]);

  if (selectedAssets.length === 0 && !onConfirmSelection) return null;

  if (!isVisible) {
    if (!showRestoreButton) return null;

    return (
      <button
        type="button"
        className="sticky-note-restore"
        style={{ left: position.x, top: position.y }}
        onClick={onToggleVisibility}
      >
        Show Selected Assets ({selectedAssets.length})
      </button>
    );
  }

  return (
    <div
      className={`card position-absolute shadow-sm sticky-note-container ${isVisible ? "visible" : ""}`}
      style={{
        left: position.x,
        top: position.y,
        width: "250px",
        maxHeight: "300px",
        zIndex: 100,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
      }}
    >
      <div
        className="card-header d-flex justify-content-between drag-handle"
        onPointerDown={handleDragStart}
        title="Click and hold to drag"
      >
        <h6 className="m-0 fs-7 fw-bold text-primary">
          Selected Assets ({selectedAssets.length})
        </h6>
        <div className="d-flex gap-3">
          <button
            type="button"
            className="cursor-pointer text-danger ms-2 fw-bold bg-transparent border-0 p-0"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onClearAll}
          >
            <FontAwesomeIcon icon={faTrash} title="Remove All" />
          </button>
          <button
            type="button"
            className="cursor-pointer fw-bold bg-transparent border-0 p-0"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onToggleVisibility}
            title="Minimize"
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        </div>
      </div>

      <div
        className="card-body p-0 overflow-auto"
        style={{ maxHeight: "350px" }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <ul className="list-group list-group-flush">
          {selectedAssets.map((assetCode, index) => (
            <li
              key={`${assetCode}-${index}`}
              className="list-group-item d-flex justify-content-between align-items-center fs-7 px-2"
              onMouseEnter={() => onAssetHover(assetCode)}
              onMouseLeave={onAssetHoverEnd}
            >
              <span className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span
                  aria-hidden="true"
                  style={{
                    minWidth: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: "#e9ecef",
                    color: "#1f1f1f",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {selectedAssetOrder[assetCode] || ""}
                </span>
                {assetCode}
              </span>
              <button
                type="button"
                className="cursor-pointer text-danger ms-2 fw-bold bg-transparent border-0 p-0"
                onClick={() => onRemoveAsset(assetCode)}
              >
                <FontAwesomeIcon icon={faTrashCan} title="Remove" />
              </button>
            </li>
          ))}
        </ul>
      </div>
      {onConfirmSelection && (
        <button
          type="button"
          className="sticky-note-confirm"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onConfirmSelection}
          disabled={confirmDisabled ?? selectedAssets.length === 0}
        >
          Confirm Selection
        </button>
      )}
    </div>
  );
};

export default StickyNoteComponent;
